import React, { useState, useEffect } from "react";
import { FiPlus, FiEdit, FiTrash2 } from "react-icons/fi";
import { sportsService, SportsLegalDocsTerm } from "../services/sportsService";

import MainHeader from "../components/MainHeader";
import ConfirmationModal from "../common/ConfirmationModal";
import { motion } from "framer-motion";
import { isStockTradingMode } from "../config";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

interface SportsLegalDocsProps {
  onBackToDashboard: () => void;
}

const TermPolicyDoc: React.FC<SportsLegalDocsProps> = ({
  onBackToDashboard,
}) => {
  const [docs, setDocs] = useState<SportsLegalDocsTerm[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState<SportsLegalDocsTerm | null>(
    null
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [docToDelete, setDocToDelete] = useState<SportsLegalDocsTerm | null>(
    null
  );

  // Form state
  const [formData, setFormData] = useState({
    type: "terms",
    appType: isStockTradingMode ? "stock" : "crypto",
    content: "", // now a string for ReactQuill
    header: "",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const response: any = await sportsService.getTermConditionDocs(
        currentPage,
        10,
        ""
      );
      setDocs(response.policies);
      setTotalPages(response.total_pages);
      setTotalDocs(response.total);
    } catch (error) {
      console.error("Error fetching legal docs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [currentPage]);

  const handleCreateDocs = async () => {
    try {
      if (!formData.content || formData.content.trim() === "") {
        alert("Please enter content.");
        return;
      }

      const docData = {
        header: formData.header,
        type: formData.type,
        appType: isStockTradingMode ? "stock" : "crypto",
        content: formData.content, // now a string
      };

      if (isEditMode && editingDocId) {
        await sportsService.updateTermCondition(editingDocId, docData as any);
      } else {
        await sportsService.createTermCondition(docData as any);
      }

      setShowCreateModal(false);
      resetForm();
      fetchDocs();
    } catch (error) {
      console.error("Error creating/updating doc:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      type: "terms",
      appType: isStockTradingMode ? "stock" : "crypto",
      content: "",
      header: "",
    });
    setIsEditMode(false);
    setEditingDocId(null);
  };

  const openEditModal = (doc: any) => {
    setEditingDoc(doc);
    setEditingDocId(doc.id);
    setIsEditMode(true);

    setFormData({
      type: doc.type || "terms",
      appType: doc.appType || (isStockTradingMode ? "stock" : "crypto"),
      header: doc.header || "",
      content:
        typeof doc.content === "string"
          ? doc.content
          : JSON.stringify(doc.content), // fallback if old docs
    });

    setShowCreateModal(true);
  };

  const handleDeleteDocs = (doc: SportsLegalDocsTerm) => {
    setDocToDelete(doc);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!docToDelete) return;
    try {
      await sportsService.deleteTermCOndition(docToDelete.id);
      setShowDeleteModal(false);
      setDocToDelete(null);
      fetchDocs();
    } catch (error) {
      console.error("Error deleting doc:", error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDocToDelete(null);
  };
  console.log(formData, "==============formData=============");
  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
  ];

  return (
    <div>
      <MainHeader onBackToDashboard={onBackToDashboard} />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 ">
          <div>
            <motion.button
              onClick={onBackToDashboard}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors duration-200"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Dashboard
            </motion.button>
          </div>
        </div>
        {/* Header end  */}
        <div className="flex items-center justify-between mb-6 ">
          <div>
            <motion.h1
              className="text-3xl font-bold text-gray-900"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Content Management
            </motion.h1>
            <motion.p className="mt-2 text-sm text-gray-600">
              Manage all static content pages like Terms & Conditions, Privacy
              Policy, and Contest Rules from a single interface.
            </motion.p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <FiPlus />
              Add Document
            </button>
          </div>
        </div>

        {/* Docs Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">Loading...</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-center">Type</th>
                  <th className="px-6 py-3 text-center">Header</th>
                  <th className="px-6 py-3 text-center">Terms Preview</th>
                  <th className="px-6 py-3 text-center">Updated At</th>
                  {/* <th className="px-6  text-centerpy-3">Status</th> */}
                  <th className="px-6 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {docs?.map((doc: any) => (
                  <tr key={doc.id}>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {doc.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">{doc.header}</td>
                    <td className="px-6 py-4 max-w-md text-sm text-gray-600">
                      {/* render a short preview */}
                      {typeof doc.content === "string"
                        ? doc.content.replace(/<[^>]+>/g, "").slice(0, 80) +
                          "..."
                        : "—"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(doc.updatedAt).toLocaleDateString()}
                    </td>
                    {/* <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        doc.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {doc.is_active ? "Active" : "Inactive"}
                    </span>
                  </td> */}
                    <td className="px-6 py-4 flex gap-2">
                      <button
                        onClick={() => openEditModal(doc)}
                        className="text-gray-600 hover:text-gray-800"
                        title="Edit document"
                      >
                        <FiEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteDocs(doc)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete document"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create Document Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {isEditMode ? "Edit Document" : "Add New Document"}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ✕
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreateDocs();
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Title
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="terms">Terms & Condition</option>
                      <option value="privacy">Privacy Policy</option>
                      <option value="contest-rules">Contest Rules</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Header Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Header
                    </label>
                    <input
                      type="text"
                      id="header"
                      name="header"
                      value={formData.header}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          header: e.target.value,
                        }))
                      }
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Header"
                    />
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content
                  </label>
                  <ReactQuill
                    value={formData.content}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, content: value }))
                    }
                    modules={modules}
                    formats={formats}
                    theme="snow"
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    {isEditMode ? "Update Document" : "Create Document"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          title="Delete Document"
          message={`Are you sure you want to delete this document? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
        />
      </div>
    </div>
  );
};

export default TermPolicyDoc;
